/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: ingestion_jobs entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the ingestion_jobs table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the ingestion_jobs API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, data_source_id, triggered_by_user_profile_id, job_type_id, job_status_id, job_name, started_at, completed_at, error_log, created_at, trust_rank_override, skip_review, auto_approve_started_at, auto_approve_completed_at, auto_approve_total, auto_approve_completed, auto_approve_failed, auto_approve_errors, rollback_status_id, rolled_back_at, rolled_back_by_user_profile_id, rollback_reason, skip_transaction_records, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, data_source_id, triggered_by_user_profile_id, job_type_id, job_status_id, rollback_status_id, rolled_back_by_user_profile_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  IngestionJobsRow,
  IngestionJobsInsert,
  IngestionJobsUpdate
} from '../../types/generated_schemas/generated-ingestion_jobs-schema.js';

export interface IngestionJobsQueryOptions extends UnifiedQueryOptions {}

export interface IngestionJobsResponse {
  data: IngestionJobsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for ingestion_jobs table operations
 *
 * @description Provides type-safe access to ingestion_jobs API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class IngestionJobsClient {
  private readonly basePath = '/ingestion-jobs';

  /**
   * Get all ingestion_jobs records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of ingestion_jobs records
   */
  async getAll(options?: IngestionJobsQueryOptions): Promise<IngestionJobsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<IngestionJobsResponse>(url);
    return response.data;
  }

  /**
   * Get a single ingestion_jobs record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the ingestion_jobs record
   */
  async getById(id: string): Promise<IngestionJobsRow> {
    const response = await httpClient.get<{
      data: IngestionJobsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new ingestion_jobs record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created ingestion_jobs record
   */
  async create(data: IngestionJobsInsert): Promise<IngestionJobsRow> {
    const response = await httpClient.post<{
      data: IngestionJobsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing ingestion_jobs record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated ingestion_jobs record
   */
  async update(id: string, data: IngestionJobsUpdate): Promise<IngestionJobsRow> {
    const response = await httpClient.put<{
      data: IngestionJobsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a ingestion_jobs record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple ingestion_jobs records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created ingestion_jobs records
   */
  async createMany(data: IngestionJobsInsert[]): Promise<IngestionJobsRow[]> {
    const response = await httpClient.post<{
      data: IngestionJobsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search ingestion_jobs with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: IngestionJobsQueryOptions
  ): Promise<IngestionJobsResponse> {
    throw new Error('Search operations are not available for ingestion_jobs - no searchable fields configured');
  }

  /**
   * Get count of ingestion_jobs records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a ingestion_jobs record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'ingestion_jobs',
      publicFields: ['id', 'data_source_id', 'triggered_by_user_profile_id', 'job_type_id', 'job_status_id', 'job_name', 'started_at', 'completed_at', 'error_log', 'created_at', 'trust_rank_override', 'skip_review', 'auto_approve_started_at', 'auto_approve_completed_at', 'auto_approve_total', 'auto_approve_completed', 'auto_approve_failed', 'auto_approve_errors', 'rollback_status_id', 'rolled_back_at', 'rolled_back_by_user_profile_id', 'rollback_reason', 'skip_transaction_records', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'data_source_id', 'triggered_by_user_profile_id', 'job_type_id', 'job_status_id', 'rollback_status_id', 'rolled_back_by_user_profile_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'skip_review', 'auto_approve_total', 'auto_approve_completed', 'auto_approve_failed', 'auto_approve_errors', 'skip_transaction_records', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const ingestionJobsClient = new IngestionJobsClient();
